import { array } from 'fp-ts';
import { flow, identity } from 'fp-ts/function';
import { DateTimeUnit } from 'luxon';
import { ServiceTimeClass, TimeUnit } from '@mews/enums';
import { ifElse } from '@mews/fp';
import { createHandler } from '@mews/std';
import { DateService } from '../dates';
import type { Date } from '../dates';
import { DateTime, DateTimeService } from '../dateTimes';
import { DateTimeUtcService } from '../dateTimesUtc';
import { Duration, DurationService, DurationUtils } from '../duration';
import { DateTimePart, unitByDateTimePart } from '../enums';

/**
 * Function for getting the beginning of a time unit
 * @param dateTime - date string
 * @param period - time unit for which you want the start time
 * @returns date time at which that time unit begins
 */
export const getTimeUnitStart = (dateTime: DateTime, period: DateTimeUnit) => DateTimeService.startOf(period)(dateTime);

/**
 * Function for converting reservation end(Utc) to correct lastTimeUnitStart(Utc)
 * @param dateTime - reservation endUtc
 * @param startOffset - startOffset of the reservation service
 * @param period - service timeunit
 * @returns occupancy interval aware last timeunit start
 */
export const getLastTimeUnitStartFromEnd = (dateTime: DateTime, startOffset: Duration, period: DateTimePart) => {
    const naiveDateTimeStart = getTimeUnitStart(dateTime, unitByDateTimePart[period]);

    const occupancyIntervalStart = DateTimeService.addSeconds(
        naiveDateTimeStart,
        DurationService.asSeconds(startOffset)
    );

    if (DateTimeService.isLessOrEqual(dateTime, occupancyIntervalStart)) {
        const pastDateTimeStart = DateTimeService.add(period)(naiveDateTimeStart, -1);
        return pastDateTimeStart;
    }
    return naiveDateTimeStart;
};

/**
 * Function for converting reservation endUtc to the start of the last service time unit
 * @param dateTime - reservation endUtc
 * @param startOffset - startOffset of the reservation service
 * @param period - service timeunit
 * @returns occupancy interval aware last service timeunit start
 */
export const getLastServiceTimeUnitStartFromEnd = (dateTime: DateTime, startOffset: Duration, period: DateTimePart) => {
    const lastTimeUnitStart = getLastTimeUnitStartFromEnd(dateTime, startOffset, period);
    const lastServiceTimeUnitStart = DateTimeService.addSeconds(
        lastTimeUnitStart,
        DurationService.asSeconds(startOffset)
    );
    return lastServiceTimeUnitStart;
};

/**
 * Function will return reservation begin of first time unit or the one before in case that startUtc of reservation overflowing endOffset of previous timeunit
 * @param startUtc - startUtc of reservation
 * @param timezone - timezone
 * @param endOffset - endOffset of the service
 * @param period - service time unit
 */
export const getFirstTimeUnitStartUtc = (
    startUtc: string,
    timezone: string,
    endOffset: string,
    period: DateTimePart
) => {
    const dateTime = DateTimeService.fromUtcString(startUtc, timezone);
    const endOffsetDuration = DurationService.parse(endOffset);
    const firstTimeUnit = getLastTimeUnitStartFromEnd(dateTime, endOffsetDuration, period);
    const firstTimeUnitUtc = DateTimeService.toUtc(firstTimeUnit).value;
    return firstTimeUnitUtc;
};

/**
 * Function will return reservation begin of the last time unit or the one before in case that endUtc of reservation is before startOffset of the next time unit
 * @param endUtc - startUtc of reservation
 * @param timezone - timezone
 * @param startOffset - startOffset of the service
 * @param period - service time unit
 */
export const getLastTimeUnitStartUtc = (
    endUtc: string,
    timezone: string,
    startOffset: string,
    period: DateTimePart
) => {
    const dateTime = DateTimeService.fromUtcString(endUtc, timezone);
    const startOffsetDuration = DurationService.parse(startOffset);
    const lastTimeUnit = getLastTimeUnitStartFromEnd(dateTime, startOffsetDuration, period);
    const lastTimeUnitUtc = DateTimeService.toUtc(lastTimeUnit).value;
    return lastTimeUnitUtc;
};

/**
 * Function for getting the beginning of the next time unit
 * @param dateTime - date string
 * @param period - time unit for which you want the start time
 * @returns date time at which that time unit begins
 */
export const getNextTimeUnitStart = (dateTime: DateTime, period: DateTimePart) => {
    const nextDay = DateTimeService.add(period)(dateTime, 1);
    return DateTimeService.startOf(unitByDateTimePart[period])(nextDay);
};

/**
 * Convert time unit to DateTimePart
 * @param utc - utc time string
 * @param timezone - timezone
 * @param period - DateTimeUnit (ie. year, month, day)
 * @returns corresponding time unit part
 */
export const getTimeUnitStartUtc = (utc: string, timezone: string, period: DateTimeUnit) => {
    const dateTime = DateTimeService.fromUtcString(utc, timezone);
    return DateTimeService.toUtc(getTimeUnitStart(dateTime, period)).value;
};

/**
 * Convert time unit to DateTimePart
 * @param utc - utc time string
 * @param timezone - timezone
 * @param period - DateTimeUnit (ie. year, month, day)
 * @returns corresponding time unit part
 */
export const getNextTimeUnitStartUtc = (utc: string, timezone: string, period: DateTimePart) => {
    const dateTime = DateTimeService.fromUtcString(utc, timezone);
    return DateTimeService.toUtc(getNextTimeUnitStart(dateTime, period)).value;
};

/**
 * Convert time unit to DateTimePart
 * @param startUtc - utc time string for start of a period
 * @param endUtc - utc time string for end of a period
 * @param timezone - timezone
 * @param period - DateTimeUnit (ie. year, month, day)
 * @returns corresponding time unit part
 */
export const getTimeUnitDiff = (startUtc: string, endUtc: string, timezone: string, period: DateTimePart) => {
    const startDateTime = DateTimeService.fromUtcString(startUtc, timezone);
    const endDateTime = DateTimeService.fromUtcString(endUtc, timezone);

    return DateTimeService.diff(period)(endDateTime, startDateTime);
};

/**
 * Convert time unit to DateTimePart
 * @param utc - utc time string for start of a period
 * @param timezone - timezone
 * @param startOffset - start offset from service value
 * @param period - DateTimeUnit (ie. year, month, day)
 * @returns corresponding time unit part
 */
export const getLastTimeUnitStartUtcFromEndUtc = (
    utc: string,
    timezone: string,
    startOffset: string,
    period: DateTimePart
) => {
    const dateTime = DateTimeService.fromUtcString(utc, timezone);
    const startOffsetDuration = DurationService.parse(startOffset);
    return DateTimeService.toUtc(getLastTimeUnitStartFromEnd(dateTime, startOffsetDuration, period)).value;
};

/**
 * @param utc - utc time string for start of a period
 * @param timezone - timezone
 * @param startOffset - start offset from service value
 * @param period - DateTimeUnit (ie. year, month, day)
 * @returns corresponding service time unit part
 */
export const getLastServiceTimeUnitStartUtcFromEndUtc = (
    utc: string,
    timezone: string,
    startOffset: string,
    period: DateTimePart
) => {
    const dateTime = DateTimeService.fromUtcString(utc, timezone);
    const startOffsetDuration = DurationService.parse(startOffset);
    return DateTimeService.toUtc(getLastServiceTimeUnitStartFromEnd(dateTime, startOffsetDuration, period)).value;
};

/**
 * Function for if the given time units are "greedy." (ie. will be used to check if an extra hour will be added to a calculation of hourly services )
 * @param startDate - beginning of period
 * @param endDate - end of period
 * @returns boolean value
 */
export const isGreedyHours = (startDate: DateTime, endDate: DateTime) =>
    !(DateTimeService.getMinutes(startDate) === 0 && DateTimeService.getMinutes(endDate) === 0);

/**
 * Function for if the given time units are "greedy." (ie. will be used to determine if an extra unit of time should be added to a total of time units)
 * @param props.timeUnit - time unit of service
 * @param props.isDayUnit - is a day use service
 * @param props.startDate - start of period
 * @param props.endDate - end of period
 * @returns boolean value
 */
export const isGreedyTimeUnit = ({
    timeUnit,
    isDayUse,
    startDate,
    endDate,
}: {
    timeUnit: TimeUnit;
    isDayUse: boolean;
    startDate: DateTime;
    endDate: DateTime;
}) => timeUnit === TimeUnit.Month || isDayUse || (timeUnit === TimeUnit.Hour && isGreedyHours(startDate, endDate));

/**
 * Converts date time to time unit
 * @param props.timePart - time part (ie. Year, Quarter, Month, Week, Date, Day)
 * @param props.greedy - if the time unit is greedy (will add +1 to the number of time units when calculating)
 * @returns a time unit for the given input
 */
export const dateTimeToTimeUnitCount = ({ timePart, greedy }: { greedy: boolean; timePart: DateTimePart }) => {
    const timeUnit = unitByDateTimePart[timePart];
    return (startDate: DateTime, endDate: DateTime) =>
        flow(
            ifElse(
                () => greedy && timePart !== DateTimePart.Hour,
                array.map(DateTimeService.startOf(timeUnit)),
                identity
            ),
            ([end, start]) => DateTimeService.diff(timePart)(...[end, start]),
            (count: number) => (greedy ? count + 1 : count)
        )([endDate, startDate]);
};

/**
 * Returns Utc of the next overlapping time unit or current time unit if overlapping does not occur
 * @param props.utc - utc representing the start of the time unit
 * @param props.endOffset - service occupancy interval end offset
 * @param props.timezone - timezone
 * @param props.timeUnit - service time unit settings
 * @returns utc string
 */
export const getNextOverlappingTimeUnitUtc = ({
    utc,
    endOffset,
    timezone,
    timeUnit,
}: {
    utc: string;
    endOffset: string;
    timezone: string;
    timeUnit: `${TimeUnit}`;
}) => {
    const isOverlappingTimeUnit = DurationUtils.isOverlappingNextTimeUnit(endOffset);
    const timePart = timeUnitToDateTimePart(timeUnit);
    const correctedEndUtc = getNextTimeUnitStartUtc(utc, timezone, timePart);

    return timeUnit === TimeUnit.Day && !isOverlappingTimeUnit ? utc : correctedEndUtc;
};

export const timeUnitToDateTimePart = createHandler<`${TimeUnit}`, DateTimePart>({
    [TimeUnit.Hour]: DateTimePart.Hours,
    [TimeUnit.Day]: DateTimePart.Day,
    [TimeUnit.Month]: DateTimePart.Month,
});

export const timeUnitToLuxonTimePart = createHandler({
    [TimeUnit.Hour]: unitByDateTimePart[DateTimePart.Hour],
    [TimeUnit.Day]: unitByDateTimePart[DateTimePart.Day],
    [TimeUnit.Month]: unitByDateTimePart[DateTimePart.Month],
});

export const serviceClassToTimeUnit = createHandler({
    [ServiceTimeClass.Night]: TimeUnit.Day,
    [ServiceTimeClass.Day]: TimeUnit.Day,
    [ServiceTimeClass.Hour]: TimeUnit.Hour,
    [ServiceTimeClass.Month]: TimeUnit.Month,
});

export const timeUnitToDateTimeUnit = createHandler<`${TimeUnit}`, DateTimeUnit>({
    [TimeUnit.Day]: 'day',
    [TimeUnit.Hour]: 'hour',
    [TimeUnit.Month]: 'month',
});

export const addTimeUnitToUtc = (utc: string, timezone: string, timeUnit: TimeUnit, count: number) => {
    const dateTimeUtc = DateTimeUtcService.parse(utc);
    const dateTime = DateTimeService.fromUtc(dateTimeUtc, timezone);
    const movedDateTime = addTimeUnitToDateTime(dateTime, timeUnit, count);

    return DateTimeService.toUtc(movedDateTime).value;
};

export const addTimeUnitToDateTime = (dateTime: DateTime, timeUnit: TimeUnit, count: number) => {
    const dateTimePart = timeUnitToDateTimePart(timeUnit);
    return DateTimeService.add(dateTimePart)(dateTime, count);
};

export const addTimeUnitToDate = (date: Date, timeUnit: TimeUnit, count: number) => {
    const dateTimePart = timeUnitToDateTimePart(timeUnit);
    return DateService.add(dateTimePart)(date, count);
};
